<template>
  <div class="d-flex flex-column align-items-center">
    <v-chart
      :style="`width: ${width}; height: ${height};`"
      autoresize
      :option="option"
      ref="bar"
      auto-resize
    />
  </div>
</template>

<script>
import { use } from "echarts/core"

// import ECharts modules manually to reduce bundle size
import { CanvasRenderer } from "echarts/renderers"
import { PieChart } from "echarts/charts"
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from "echarts/components"
import VChart from "vue-echarts"

use([
  CanvasRenderer,
  PieChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
])

export default {
  name: "AtomDoughnutChartV2",
  components: { VChart },
  props: {
    dataList: {
      type: Array,
      required: true,
      // example: [
      //   {
      //     name: 'A',
      //     value: 120,
      //     color: '#7367F0',
      //   },
      // ],
      // note: important value is the last
      default: [],
    },
    title: {
      type: String,
      required: false,
      default: "",
    },
    subTitle: {
      type: String,
      required: false,
      default: "",
    },
    isTextInner: {
      type: Boolean,
      required: false,
      default: false,
    },
    radius: {
      type: String,
      required: false,
      default: "40%",
    },
    innerRadius: {
      type: String,
      required: false,
      default: "70%",
    },
    isShowTextCenterWhenHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    fontSize: {
      type: String,
      required: false,
      default: "14",
    },
    isTooltip: {
      type: Boolean,
      required: false,
      default: true,
    },
    isAnimationHover: {
      type: Boolean,
      required: false,
      default: false,
    },
    width: {
      type: String,
      required: false,
      default: '200px'
    },
    height: {
      type: String,
      required: false,
      default: '200px'
    },
  },
  data() {
    return {
      option: {},
    }
  },
  watch: {
    dataList: {
      deep: true,
      handler() {
        this.initialEcharts()
      }
    }
  },
  mounted() {
    this.initialEcharts()
  },
  methods: {
    pieColor(color) {
      if (color === "primary") return "#7367F0"
      if (color === "danger") return "#EA5455"
      if (color === "success") return "#28C76F"
      if (color === "secondary") return "#82868B"
      if (color === "warning") return "#FF9F43"
      if (color === "info") return "#00CFE8"
      if (color === "light-primary") return "#EEEDFD"
      if (color === "light-danger") return "#FCEAEB"
      if (color === "light-success") return "#E5F8EE"
      if (color === "light-secondary") return "#F0F1F1"
      if (color === "light-warning") return "#FFF3E8"
      if (color === "light-info") return "#E0F9FC"
      return color
    },

    textStyle(style) {
      const { color, fontSize, fontWeight } = style

      return {
        textFamily: "inherit",
        color: color ?? "#5e5873",
        fontSize: fontSize ?? this.fontSize,
        fontWeight: fontWeight ?? "400",
        ...style,
      }
    },

    getLabel() {
      const label = {
        position: "center",
        fontSize: this.fontSize,
        fontWeight: 500,
      }

      if (this.isTextInner) {
        return {
          ...label,
          position: "inner"
        }
      }

      if (this.isShowTextCenterWhenHover) {
        return {
          ...label,
          show: false,
        }
      }

      return null
    },

    getEmphasis() {
      if (this.isAnimationHover) {
        return {
          label: {
            show: this.isShowTextCenterWhenHover,
            fontSize: this.fontSize,
            fontWeight: 500,
          },
        }
      }

      return null
    },

    initialEcharts() {
      this.option = {
        title: {
          text: this.title ?? undefined,
          subtext: this.subTitle ?? undefined,
          left: "center",
          textStyle: this.textStyle({ fontWeight: "500" }),
          subtextStyle: this.textStyle({ color: "#6e6b7b" }),
        },
        tooltip: this.isTooltip ? {
          trigger: "item",
        }: null,
        series: [
          {
            type: "pie",
            size: "100%",
            radius: [this.radius, this.innerRadius],
            label: this.getLabel(),
            emphasis: this.getEmphasis(),
            avoidLabelOverlap: false,
            silent: true,
            data: this.dataList.map((data, index) => {
              return {
                name: data.name,
                value: data.value,
                itemStyle: {
                  color: this.pieColor(data.color),
                  shadowColor: 'rgba(115, 103, 240, 0.7)',
                  shadowBlur: index === 0 ? 20 : 0,
                  shadowOffsetX: index === 0 ? 2 : 0,
                  shadowOffsetY: index === 0 ? 10 : 0,
                },
              }
            }).reverse(),
          },
        ],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
label {
  margin-bottom: 0;
}

.atom-pie-chart__container {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
}
</style>
